import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useAuthContext } from "../hooks/useAuthContext";
import { useLogout } from "../hooks/useLogout";
import axios from "axios";
import { SUBSCRIBERS_BASE_URL } from "../services/SubscribersService";
import { USERS_BASE_URL } from "../services/SignUpService";
import { COURSE_BASE_URL } from "../services/courseService";
import { BLOG_BASE_URL } from "../services/blogService";
//  import "./DashboardStyle.css";

const Dashboard = () => {
  const { user } = useAuthContext();
  const { logout } = useLogout();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [subscribers, setSubscribers] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [courses, setCourses] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMenu, setActiveMenu] = useState("");

  // Format roles
  const formattedRoles = Array.isArray(user.role)
    ? user.role
        .map((r) => (typeof r === "object" ? r.name || JSON.stringify(r) : r))
        .join(", ")
    : user.role;

  const fetchAllData = async () => {
    try {
      const [subscribersRes, usersRes, coursesRes, blogsRes] =
        await Promise.all([
          axios.get(`${SUBSCRIBERS_BASE_URL}/api/subscribers/list`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
          axios.get(`${USERS_BASE_URL}/api/users/list`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
          axios.get(`${COURSE_BASE_URL}/api/courses/list`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
          axios.get(`${BLOG_BASE_URL}/api/blogs/list`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
        ]);
      setSubscribers(subscribersRes.data);
      setUsersList(usersRes.data);
      setCourses(coursesRes.data);
      setBlogs(blogsRes.data);
    } catch (err) {
      setError("Unable to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // Set active menu based on current path
    const path = location.pathname.split("/").pop();
    setActiveMenu(path === "dashboard" ? "home" : path);
  }, [user.token, location.pathname]);

  const handleLogout = () => logout();
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const handleLinkClick = (menu) => {
    setActiveMenu(menu);
  };

  const isNestedRoute = location.pathname !== "/api/dashboard";

  // Stats cards data
  const stats = [
    {
      title: "Subscribers",
      value: subscribers.length,
      icon: "fa-users",
      color: "bg-primary",
    },
    {
      title: "Users",
      value: usersList.length,
      icon: "fa-users",
      color: "bg-success",
    },
    {
      title: "Courses",
      value: courses.length,
      icon: "fa-book",
      color: "bg-info",
    },
    {
      title: "Blogs",
      value: blogs.length,
      icon: "fa-book",
      color: "bg-warning",
    },
  ];

  return (
    <div className="d-flex" style={{ minHeight: "100vh" }}>
      {/* Sidebar */}
      <div
        className={`bg-light text-dark ${
          sidebarOpen ? "sidebar-open" : "sidebar-collapsed"
        }`}
        style={{
          width: sidebarOpen ? "250px" : "60px",
          transition: "width 0.3s",
          position: "sticky",
          top: 0,
          height: "100vh",
          overflowY: "auto",
        }}
      >
        <div className="p-3 d-flex justify-content-between align-items-center">
          {sidebarOpen && <h5 className="m-0">Dashboard</h5>}
          <button
            className="btn btn-link text-dark p-0"
            onClick={toggleSidebar}
          >
            <i
              className={`fa fa-${sidebarOpen ? "times text-dark" : "bars"}`}
            ></i>
          </button>
        </div>
        <hr className="bg-secondary m-0" />
        <ul className="nav flex-column p-2">
          <li className="nav-item">
            <Link
              to="/api/dashboard"
              className={`nav-link text-white d-flex align-items-center ${
                activeMenu === "home" ? "active" : ""
              }`}
              onClick={() => handleLinkClick("home")}
            >
              <i
                className="fa fa-home mr-3"
                style={{ width: "20px", textAlign: "center" }}
              ></i>
              {sidebarOpen && "Home"}
            </Link>
          </li>

          <li className="nav-item">
            <Link
              to="/api/dashboard/my-courses"
              className={`nav-link text-white d-flex align-items-center ${
                activeMenu === "my-courses" ? "active" : ""
              }`}
              onClick={() => handleLinkClick("my-courses")}
            >
              <i
                className="fa fa-list mr-3 me-1"
                style={{ width: "20px", textAlign: "center" }}
              ></i>
              {sidebarOpen && "My Courses"}
            </Link>
          </li>

          {user?.isAdmin && (
            <>
              <li className="nav-item dropdown">
                <a
                  className={`nav-link text-white d-flex align-items-center dropdown-toggle ${
                    activeMenu.startsWith("course") ? "active" : ""
                  }`}
                  data-bs-toggle="dropdown"
                >
                  <i
                    className="fa fa-book mr-3"
                    style={{ width: "20px", textAlign: "center" }}
                  ></i>
                  {sidebarOpen && "Courses"}
                </a>
                <div className="dropdown-menu bg-light">
                  <Link
                    className={`dropdown-item text-white ${
                      activeMenu === "course-table" ? "active" : ""
                    }`}
                    to="/api/dashboard/course-table"
                    onClick={() => handleLinkClick("course-table")}
                  >
                    <i className="fa fa-list me-2"></i> Course List
                  </Link>
                  <div className="dropdown-divider bg-secondary"></div>
                  <Link
                    className={`dropdown-item text-white ${
                      activeMenu === "add-course" ? "active" : ""
                    }`}
                    to="/api/dashboard/add-course"
                    onClick={() => handleLinkClick("add-course")}
                  >
                    <i className="fa fa-plus me-2"></i> Add Course
                  </Link>
                </div>
              </li>

              <li className="nav-item dropdown">
                <a
                  className={`nav-link text-white d-flex align-items-center dropdown-toggle ${
                    activeMenu === "users" ? "active" : ""
                  }`}
                  data-bs-toggle="dropdown"
                >
                  <i
                    className="fa fa-users mr-3 me-1"
                    style={{ width: "20px", textAlign: "center" }}
                  ></i>
                  {sidebarOpen && "Users"}
                </a>
                <div className="dropdown-menu bg-light">
                  <Link
                    className={`dropdown-item text-white ${
                      activeMenu === "users" ? "active" : ""
                    }`}
                    to="/api/dashboard/users"
                    onClick={() => handleLinkClick("users")}
                  >
                    <i className="fa fa-list me-1"></i> Users List
                  </Link>
                </div>
              </li>

              <li className="nav-item dropdown">
                <a
                  className={`nav-link text-white d-flex align-items-center dropdown-toggle ${
                    activeMenu.startsWith("blog") ? "active" : ""
                  }`}
                  data-bs-toggle="dropdown"
                >
                  <i
                    className="fa fa-th mr-3"
                    style={{ width: "20px", textAlign: "center" }}
                  ></i>
                  {sidebarOpen && "Blogs"}
                </a>
                <div className="dropdown-menu bg-light">
                  <Link
                    className={`dropdown-item text-white ${
                      activeMenu === "blogs" ? "active" : ""
                    }`}
                    to="/api/dashboard/blogs"
                    onClick={() => handleLinkClick("blogs")}
                  >
                    <i className="fa fa-list-ul me-2"></i> Blog List
                  </Link>
                  <div className="dropdown-divider bg-secondary"></div>
                  <Link
                    className={`dropdown-item text-white ${
                      activeMenu === "create" ? "active" : ""
                    }`}
                    to="/api/dashboard/create"
                    onClick={() => handleLinkClick("create")}
                  >
                    <i className="fa fa-plus me-2"></i> Add Blog
                  </Link>
                </div>
              </li>

              <li className="nav-item">
                <Link
                  to="/api/dashboard/subscribers"
                  className={`nav-link text-white d-flex align-items-center ${
                    activeMenu === "subscribers" ? "active" : ""
                  }`}
                  onClick={() => handleLinkClick("subscribers")}
                >
                  <i
                    className="fa fa-list-ul mr-3 me-1"
                    style={{ width: "20px", textAlign: "center" }}
                  ></i>
                  {sidebarOpen && "Subscribers"}
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  to="/api/dashboard/all-contacts"
                  className={`nav-link text-white d-flex align-items-center ${
                    activeMenu === "all-contacts" ? "active" : ""
                  }`}
                  onClick={() => handleLinkClick("all-contacts")}
                >
                  <i
                    className="fa fa-address-book mr-3 me-1"
                    style={{ width: "20px", textAlign: "center" }}
                  ></i>
                  {sidebarOpen && "Contacts"}
                </Link>
              </li>
            </>
          )}

          <hr className="bg-secondary mt-auto" />

          {/* Profile Links */}
          <li className="nav-item">
            <Link
              to="/api/dashboard/my-profile"
              className={`nav-link text-white d-flex align-items-center ${
                activeMenu === "my-profile" ? "active" : ""
              }`}
              onClick={() => handleLinkClick("my-profile")}
            >
              <i
                className="fa fa-user mr-3 me-1"
                style={{ width: "20px", textAlign: "center" }}
              ></i>
              {sidebarOpen && "My Profile"}
            </Link>
          </li>

          <li className="nav-item">
            <Link
              to="/api/dashboard/setting"
              className={`nav-link text-white d-flex align-items-center ${
                activeMenu === "setting" ? "active" : ""
              }`}
              onClick={() => handleLinkClick("setting")}
            >
              <i
                className="fa fa-cog mr-3 me-1"
                style={{ width: "20px", textAlign: "center" }}
              ></i>
              {sidebarOpen && "Settings"}
            </Link>
          </li>

          <li className="nav-item">
            <Link
              to="#"
              className={`nav-link text-white d-flex align-items-center ${
                activeMenu === "messages" ? "active" : ""
              }`}
              onClick={() => handleLinkClick("messages")}
            >
              <i
                className="fa fa-envelope mr-3 me-1"
                style={{ width: "20px", textAlign: "center" }}
              ></i>
              {sidebarOpen && "Messages"}
            </Link>
          </li>

          <li className="nav-item">
            <Link
              className="nav-link text-danger d-flex align-items-center"
              onClick={() => {
                handleLogout();
                handleLinkClick();
              }}
            >
              <i
                className="fa fa-sign-out mr-3 me-1"
                style={{ width: "20px", textAlign: "center" }}
              ></i>
              {sidebarOpen && "Logout"}
            </Link>
          </li>
        </ul>
      </div>

      {/* Main Content */}
      <div className="flex-grow-1" style={{ overflowX: "hidden" }}>
        {/* Top Navigation */}
        <nav className="navbar navbar-expand-lg navbar-light bg-light">
          <div className="container-fluid">
            {/* <button className="btn btn-light d-lg-none" onClick={toggleSidebar}>
              <i className="fa fa-bars"></i>
            </button> */}
            <div className="d-flex align-items-center ms-auto">
              <div className="me-3">
                <i className="fa fa-bell"></i>
              </div>
              <div className="me-3">
                <i className="fa fa-envelope"></i>
              </div>
              <div className="dropdown">
                <button
                  className="btn dropdown-toggle d-flex align-items-center"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                >
                  <div className="avatar-sm me-2">
                    {user?.firstName?.charAt(0)}
                    {user?.lastName?.charAt(0)}
                  </div>
                  <span>{user?.firstName}</span>
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <Link
                      className="dropdown-item"
                      to="/api/dashboard/my-profile"
                    >
                      Profile
                    </Link>
                  </li>
                  <li>
                    <Link className="dropdown-item" to="/api/dashboard/setting">
                      Settings
                    </Link>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <button className="dropdown-item" onClick={handleLogout}>
                      Logout
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content Area */}
        <div className="container-fluid p-4">
          {!isNestedRoute ? (
            <>
              {/* Welcome Card */}
              <div className="card mb-4">
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <h4 className="mb-1">
                        Welcome back, {user?.firstName} {user?.lastName}!
                      </h4>
                      <p className="text-muted mb-0">
                        {new Date().toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    </div>
                    <div className="col-md-4 text-md-end">
                      <div className="avatar-lg">
                        {user?.firstName?.charAt(0)}
                        {user?.lastName?.charAt(0)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              {user?.isAdmin && (
                <div className="row mb-4">
                  {stats.map((stat, index) => (
                    <div key={index} className="col-md-6 col-xl-3 mb-4">
                      <div className={`card text-white ${stat.color}`}>
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="card-title">{stat.title}</h6>
                              <h2 className="card-text mb-0">{stat.value}</h2>
                            </div>
                            <i className={`fa ${stat.icon} fa-2x`}></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* User Profile Card */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Your Profile</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <tbody>
                        <tr>
                          <th scope="row">Name</th>
                          <td>
                            {user?.firstName} {user?.lastName}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Email</th>
                          <td>{user?.email}</td>
                        </tr>
                        <tr>
                          <th scope="row">Role</th>
                          <td>{formattedRoles}</td>
                        </tr>
                        <tr>
                          <th scope="row">Admin</th>
                          <td>
                            <span
                              className={`badge ${
                                user?.isAdmin ? "bg-success" : "bg-secondary"
                              }`}
                            >
                              {user?.isAdmin ? "Yes" : "No"}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Joined</th>
                          <td>
                            {new Date(user?.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              }
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
